/**
 * TREATX-AGREEMENT-MANAGEMENT-ADMIN-UI-01
 * ---------------------------------------
 * Admin surface over the existing Agreement Management MVP API.
 *
 * Screens (all in this file, panel-switched by internal state — no
 * new router complexity, no new context providers):
 *   1. Library         — GET /api/admin/agreements
 *   2. Detail          — GET /api/admin/agreements/{key}
 *   3. Version history — GET /api/admin/agreements/{key}/versions
 *   4. Draft editor    — POST /api/admin/agreements/{key}/versions
 *                        PATCH /api/admin/agreements/{key}/versions/{ver}
 *   5. Publish draft   — POST /api/admin/agreements/{key}/versions/{ver}/publish
 *   6. Archive         — POST /api/admin/agreements/{key}/archive
 *   7. Acceptance log  — GET /api/admin/agreements/{key}/acceptances
 *   8. Re-consent      — GET /api/admin/agreements/{key}/reconsent-summary
 *
 * Out of scope (per sprint contract): PDF upload, CSV export,
 * rollback UI, complex i18n editor, bulk email notification.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, History, ShieldAlert, Users, Loader2,
  RefreshCw, Edit3, Send, Archive, ClipboardList, AlertTriangle,
  CheckCircle2, Eye, FileUp, Upload, Globe2, Rocket, EyeOff, Lock,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { SafeHtml } from '../../components/security/SafeHtml';
import { api } from '../../utils/api';
import { useTreatXAdminAuth } from '../../context/TreatXAdminAuthContext';
import { PublishWizard, RetirementActionsModal, ReconsentConfigModal } from './LegalGuidedFlows';
import { LegalRegistryAllPanel } from './LegalRegistryAll';

const PANELS = {
  LIBRARY: 'library',
  REGISTRY: 'registry',
  DETAIL: 'detail',
  VERSIONS: 'versions',
  EDITOR: 'editor',
  ACCEPTANCES: 'acceptances',
  RECONSENT: 'reconsent',
};

const humanDate = (iso) => {
  if (!iso) return '—';
  try { return formatDateTimeLocale(iso); } catch { return iso; }
};

const shortSha = (sha) => (sha ? `${sha.slice(0, 12)}…` : '—');

// Canonical public route map (display-only; where a legal document is
// surfaced on the public site). Reused by the activation confirmation.
const PUBLIC_ROUTES = {
  privacy_policy: '/privacy',
  terms_of_service: '/terms',
  cookie_policy: '/cookie-policy',
  refund_policy: '/refund-policy',
  health_tourism_info: '/health-tourism-info',
};

const isPublicStatus = (s) => s === 'active' || s === 'published';

// ── Thin lifecycle status bar (READ-ONLY, derived) ──────────────
// Every chip's state is derived from canonical backend state passed in
// via `steps`. No persisted lifecycle status, no state machine.
const LC_STATE_CLS = {
  done: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  action: 'bg-amber-100 text-amber-800 border-amber-300 ring-1 ring-amber-300',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  pending: 'bg-slate-100 text-slate-500 border-slate-200',
  error: 'bg-rose-100 text-rose-700 border-rose-300',
};

const LifecycleBar = ({ steps }) => (
  <div className="flex flex-wrap items-center gap-2 mb-5 bg-white border rounded-xl p-3" data-testid="agreement-lifecycle-bar">
    {steps.map((s, i) => (
      <React.Fragment key={s.id}>
        <div
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs ${LC_STATE_CLS[s.state] || LC_STATE_CLS.pending}`}
          data-testid={`lifecycle-step-${s.id}`}
          data-state={s.state}
          title={s.hint || ''}
        >
          <span className="font-semibold">{i + 1}</span>
          <span className="font-medium">{s.label}</span>
          {s.detail && <span className="opacity-75">· {s.detail}</span>}
        </div>
        {i < steps.length - 1 && <span className="text-slate-300">—</span>}
      </React.Fragment>
    ))}
  </div>
);

const AudienceBadge = ({ audience }) => (
  <Badge variant="outline" data-testid={`agreement-audience-${audience || 'all'}`}>
    {audience || 'all'}
  </Badge>
);

const StatusBadge = ({ status }) => {
  const color = status === 'archived'
    ? 'bg-slate-100 text-slate-600'
    : (status === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700');
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {status || 'active'}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────
// Language / locale display helpers (OWNER UAT — legal language clarity)
// Reads STRICTLY from the canonical per-locale body map already stored
// on `platform_content_versions.body` (see `agreement_management_routes`
// and `legal_admin_multilingual_routes`). No fabrication.
// ─────────────────────────────────────────────────────────────
const LOCALE_LABEL = {
  en: 'English', tr: 'Türkçe', de: 'Deutsch', es: 'Español', fr: 'Français',
  it: 'Italiano', ar: 'العربية', ru: 'Русский',
};

const localeUnion = (locales, supported = []) => {
  const s = new Set();
  (locales?.published || []).forEach((l) => s.add(l));
  (locales?.draft || []).forEach((l) => s.add(l));
  (locales?.mandatory || []).forEach((l) => s.add(l));
  if (locales?.controlling) s.add(locales.controlling);
  (supported || []).forEach((l) => s.add(l));
  if (s.size === 0) { s.add('en'); }
  return Array.from(s);
};

const LocaleBadgeRow = ({ locales, contentKey, compact = false, t, supported = [] }) => {
  const list = localeUnion(locales, supported);
  const published = new Set(locales?.published || []);
  const draft = new Set(locales?.draft || []);
  const mandatory = new Set(locales?.mandatory || []);
  return (
    <div className={`flex flex-wrap gap-1.5 ${compact ? 'text-[10px]' : 'text-xs'}`} data-testid={`agreement-locales-${contentKey}`}>
      {list.map((loc) => {
        const label = LOCALE_LABEL[loc] || loc.toUpperCase();
        let state = 'missing';
        let cls = 'bg-slate-100 text-slate-500 border-slate-200';
        if (published.has(loc)) { state = 'published'; cls = 'bg-emerald-50 text-emerald-800 border-emerald-200'; }
        else if (draft.has(loc)) { state = 'draft'; cls = 'bg-amber-50 text-amber-800 border-amber-200'; }
        const stateLabel = t(`agreements.languages.${state}`);
        const req = mandatory.has(loc) ? '*' : '';
        return (
          <span
            key={loc}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border ${cls}`}
            data-testid={`agreement-locale-${contentKey}-${loc}-${state}`}
            title={mandatory.has(loc) ? t('agreements.languages.card.mandatory') : ''}
          >
            <span className="font-medium">{label}{req}</span>
            <span className="opacity-70">· {stateLabel}</span>
          </span>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// 1. LIBRARY
// ─────────────────────────────────────────────────────────────
const LibraryPanel = ({ agreements, loading, error, onOpen, onReload, onOpenWizard, supportedLocales = [] }) => {
  const { t } = useTranslation();
  // TREATX-OWNER-UAT-AGREEMENTS-FILTER-01:
  // Default = active-only (matches the previously-shipped behaviour).
  // Owner can deliberately retrieve Archived (and All including drafts)
  // via the status filter below. Client-side filter is safe here because
  // the backend already returns the full library and there are ~10
  // agreements — pagination is not required at this scale.
  const [statusFilter, setStatusFilter] = useState('active');
  const filtered = React.useMemo(() => {
    if (!Array.isArray(agreements)) return [];
    if (statusFilter === 'all') return agreements;
    if (statusFilter === 'active') {
      // Treat null/undefined/'published'/'active' as active. Archived
      // and draft are excluded unless explicitly requested.
      return agreements.filter((a) => !a.status || a.status === 'active' || a.status === 'published');
    }
    return agreements.filter((a) => a.status === statusFilter);
  }, [agreements, statusFilter]);
  const counts = React.useMemo(() => {
    const c = { all: agreements?.length || 0, active: 0, archived: 0, draft: 0 };
    (agreements || []).forEach((a) => {
      if (!a.status || a.status === 'active' || a.status === 'published') c.active += 1;
      else if (a.status === 'archived') c.archived += 1;
      else if (a.status === 'draft') c.draft += 1;
    });
    return c;
  }, [agreements]);
  return (
  <div>
    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Agreement Library</h2>
        <p className="text-xs text-slate-500">
          All legal agreements published by TreatX. Click any row to inspect and manage.
        </p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"
          data-testid="agreements-status-filter"
        >
          <option value="active">Active ({counts.active})</option>
          <option value="archived">Archived ({counts.archived})</option>
          <option value="draft">Draft ({counts.draft})</option>
          <option value="all">All ({counts.all})</option>
        </select>
        <Button
          size="sm"
          onClick={onOpenWizard}
          data-testid="agreements-publish-wizard-btn"
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Upload className="h-3 w-3 mr-1"/> Publish new / new version
        </Button>
        <a
          href="#/treatx-admin/legal-documents/usage"
          data-testid="agreements-usage-link"
          className="inline-flex items-center text-xs font-medium px-3 py-1.5 rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
        >
          Usage
        </a>
        <a
          href="#/treatx-admin/legal-policies"
          data-testid="agreements-policies-link"
          className="inline-flex items-center text-xs font-medium px-3 py-1.5 rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
        >
          Legal policies
        </a>
        <Button
          variant="outline"
          size="sm"
          onClick={onReload}
          disabled={loading}
          data-testid="agreements-reload-btn"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          <span className="ml-2">Reload</span>
        </Button>
      </div>
    </div>

    {/* TREATX-OWNER-UAT-AGREEMENTS-ACTIONS-REACHABLE-01:
        The action column contains 4 buttons (View / Draft / History / Records).
        Wrap the whole table in overflow-x-auto AND keep the action cell on a
        single line — Owner can now horizontally scroll to reach every action
        on narrow viewports, matching the existing responsive-table pattern
        used elsewhere in the admin (BeforeAfterModerationQueue, VipServices). */}
    <div className="bg-white border rounded-2xl overflow-x-auto" data-testid="agreements-library-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
          <tr>
            <th className="text-left px-4 py-2">Key / Title</th>
            <th className="text-left px-4 py-2">Audience</th>
            <th className="text-left px-4 py-2">{t('agreements.library.publishedVersion')}</th>
            <th className="text-left px-4 py-2">{t('agreements.library.publicStatus')}</th>
            <th className="text-left px-4 py-2">{t('agreements.languages.label')}</th>
            <th className="text-left px-4 py-2">{t('agreements.library.workingDraft')}</th>
            <th className="text-left px-4 py-2">Updated</th>
            <th className="text-right px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>
          )}
          {!loading && error && (
            <tr><td colSpan={8} className="px-4 py-6 text-center" data-testid="agreements-load-error">
              <div className="inline-flex flex-col items-center gap-2 text-sm">
                <span className="text-rose-700 font-medium">
                  Couldn’t load agreements{error.status ? ` (HTTP ${error.status})` : ''}.
                </span>
                <span className="text-xs text-slate-500 max-w-md break-words">
                  {String(error.detail)}
                  {error.correlation ? ` · ref: ${error.correlation}` : ''}
                </span>
                <button
                  className="mt-1 px-3 py-1.5 rounded border bg-white text-xs hover:bg-slate-50"
                  onClick={onReload}
                  data-testid="agreements-load-error-retry"
                >
                  Retry
                </button>
              </div>
            </td></tr>
          )}
          {!loading && !error && filtered.length === 0 && (
            <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">
              No agreements match the current filter ({statusFilter}).
              {statusFilter !== 'all' && agreements?.length > 0 && (
                <>{' '}<button className="underline text-blue-600" onClick={() => setStatusFilter('all')} data-testid="agreements-filter-reset">Show all</button>.</>
              )}
            </td></tr>
          )}
          {!loading && !error && filtered.map((a) => (
            <tr key={a.content_key} className="border-t hover:bg-slate-50" data-testid={`agreement-row-${a.content_key}`}>
              <td className="px-4 py-3">
                <div className="font-medium text-slate-900">{a.content_key}</div>
                <div className="text-xs text-slate-500">{a.content_type}</div>
              </td>
              <td className="px-4 py-3"><AudienceBadge audience={a.audience} /></td>
              <td className="px-4 py-3" data-testid={`agreement-published-version-${a.content_key}`}>
                {a.current_version
                  ? <span className="text-slate-700">v{a.current_version} · <span className="text-emerald-700 text-xs font-medium">{t('agreements.languages.published')}</span></span>
                  : <span className="text-xs text-slate-400">{t('agreements.library.notPublishedShort')}</span>}
              </td>
              <td className="px-4 py-3" data-testid={`agreement-public-status-${a.content_key}`}>
                {a.status === 'archived'
                  ? <StatusBadge status="archived" />
                  : (isPublicStatus(a.status)
                      ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">{t('agreements.library.statusActive')}</span>
                      : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">{t('agreements.library.statusDraftNotPublic')}</span>)}
                {!isPublicStatus(a.status) && a.status !== 'archived' && a.current_version && (
                  <div className="text-[10px] text-slate-400 mt-0.5">{t('agreements.library.notPubliclyVisible')}</div>
                )}
              </td>
              <td className="px-4 py-3 min-w-[220px]">
                <LocaleBadgeRow locales={a.locales} contentKey={a.content_key} compact t={t} supported={supportedLocales} />
              </td>
              <td className="px-4 py-3">
                {a.pending_draft
                  ? <Badge className="bg-amber-100 text-amber-700" data-testid={`agreement-draft-badge-${a.content_key}`}>
                      v{a.pending_draft.version} {t('agreements.languages.draft').toLowerCase()}
                    </Badge>
                  : <span className="text-xs text-slate-400">—</span>}
              </td>
              <td className="px-4 py-3 text-xs text-slate-500">{humanDate(a.updated_at)}</td>
              <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onOpen(a.content_key, PANELS.DETAIL)}
                  data-testid={`agreement-view-btn-${a.content_key}`}
                >
                  <Eye className="h-3 w-3 mr-1" /> View
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onOpen(a.content_key, PANELS.EDITOR)}
                  data-testid={`agreement-edit-btn-${a.content_key}`}
                >
                  <Edit3 className="h-3 w-3 mr-1" /> Draft
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onOpen(a.content_key, PANELS.VERSIONS)}
                  data-testid={`agreement-versions-btn-${a.content_key}`}
                >
                  <History className="h-3 w-3 mr-1" /> History
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onOpen(a.content_key, PANELS.ACCEPTANCES)}
                  data-testid={`agreement-acceptances-btn-${a.content_key}`}
                >
                  <ClipboardList className="h-3 w-3 mr-1" /> Records
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
  );
};

// ─────────────────────────────────────────────────────────────
// 2. DETAIL (current published version)
// ─────────────────────────────────────────────────────────────
const DetailPanel = ({ contentKey, isSuperAdmin, onNavigate, onArchive, onOpenRetire, onOpenReconsent, onOpenReplaceWizard }) => {
  const { t } = useTranslation();
  const [meta, setMeta] = useState(null);
  const [publishedVersion, setPublishedVersion] = useState(null);
  const [allVersions, setAllVersions] = useState([]);
  const [localeOverview, setLocaleOverview] = useState(null);
  const [bindingCount, setBindingCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);
  const [privateOpen, setPrivateOpen] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [m, vs, lo, sl, rc] = await Promise.all([
        api.get(`/admin/agreements/${contentKey}`),
        api.get(`/admin/agreements/${contentKey}/versions`),
        api.get(`/admin/legal/documents/${encodeURIComponent(contentKey)}/locale-overview`).catch(() => null),
        api.get(`/admin/legal/supported-locales`).catch(() => null),
        api.get(`/admin/legal/documents/${encodeURIComponent(contentKey)}/retirement-check`).catch(() => null),
      ]);
      const metaRow = m.data?.agreement || null;
      if (!metaRow) throw new Error('Agreement not found');
      setMeta(metaRow);
      const versions = vs.data?.versions || [];
      setAllVersions(versions);
      const cur = metaRow.current_version;
      const active = typeof cur === 'number'
        ? versions.find(v => v.version === cur)
        : versions.find(v => v.status !== 'draft') || versions[0];
      setPublishedVersion(active || null);
      // Merge supported locales into the overview so Owner can start a
      // brand-new locale (e.g. Türkçe) that has no stored body yet.
      const overview = lo?.data ? { ...lo.data } : null;
      const supported = sl?.data?.supported_locales || [];
      if (overview) overview._supported_locales = supported;
      setLocaleOverview(overview);
      const blockers = rc?.data?.blocked_by_bindings;
      setBindingCount(Array.isArray(blockers) ? blockers.length : null);
    } catch (e) {
      // Explicit ERROR state — a failed load is NEVER rendered as an
      // empty / zero / complete lifecycle.
      setLoadError({
        status: e?.response?.status || 0,
        detail: (e?.response?.data && (e.response.data.detail || e.response.data.message)) || e?.message || 'Request failed',
      });
    } finally {
      setLoading(false);
    }
  }, [contentKey]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="text-slate-500 py-10 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>;
  }
  if (loadError) {
    return (
      <div className="py-10 text-center" data-testid="agreement-detail-error">
        <div className="inline-flex flex-col items-center gap-2 text-sm">
          <span className="text-rose-700 font-medium">
            {t('agreements.lifecycle.loadError')}{loadError.status ? ` (HTTP ${loadError.status})` : ''}.
          </span>
          <span className="text-xs text-slate-500 max-w-md break-words">{String(loadError.detail)}</span>
          <button
            className="mt-1 px-3 py-1.5 rounded border bg-white text-xs hover:bg-slate-50"
            onClick={load}
            data-testid="agreement-detail-retry"
          >
            {t('agreements.lifecycle.retry')}
          </button>
        </div>
      </div>
    );
  }
  if (!meta) return <div className="text-red-600 py-10 text-center">Agreement not found.</div>;

  const bodyEn = (publishedVersion?.body || {}).en || '';
  const shaEn = (publishedVersion?.body_sha256 || {}).en || null;

  // ── Derived lifecycle state (READ-ONLY, from canonical fields) ──
  const pol = localeOverview?.policy || {};
  const controlling = pol.controlling_locale || 'en';
  const mandatory = (pol.mandatory_locales && pol.mandatory_locales.length) ? pol.mandatory_locales : ['en'];
  const pubLocales = (localeOverview?.published?.locales_present)
    || Object.keys(publishedVersion?.body || {}).filter(k => String((publishedVersion.body || {})[k] || '').trim());
  const draftLocales = localeOverview?.draft?.locales_present || [];
  const publicNow = isPublicStatus(meta.status);
  const publishedExists = !!publishedVersion && (
    publishedVersion.status === 'published'
    || !!publishedVersion.published_at
    || (typeof meta.current_version === 'number' && publishedVersion.version === meta.current_version)
  );
  const contentReady = pubLocales.length > 0 || draftLocales.length > 0
    || !!String((publishedVersion?.body || {})[controlling] || '').trim();
  const langBasis = publishedExists ? pubLocales : (draftLocales.length ? draftLocales : pubLocales);
  const missingMandatory = mandatory.filter(l => !langBasis.includes(l));

  // Working / superseded drafts — DISPLAY-ONLY derivation. A draft below
  // the current published version is "superseded" (never persisted).
  const drafts = allVersions.filter(v => v.status === 'draft');
  const curV = typeof meta.current_version === 'number' ? meta.current_version : null;
  const supersededDrafts = (curV != null && publishedExists) ? drafts.filter(v => v.version < curV) : [];
  const workingDraft = drafts
    .filter(v => !(curV != null && publishedExists && v.version < curV))
    .sort((a, b) => b.version - a.version)[0] || null;

  const publicRoute = PUBLIC_ROUTES[contentKey] || null;

  const lifecycleSteps = [
    {
      id: 'content', label: t('agreements.lifecycle.step.content'),
      state: contentReady ? 'done' : 'pending',
      detail: contentReady ? undefined : t('agreements.lifecycle.contentMissing'),
    },
    {
      id: 'languages', label: t('agreements.lifecycle.step.languages'),
      state: missingMandatory.length === 0 && langBasis.length > 0 ? 'done' : 'warning',
      detail: (missingMandatory.length === 0 && langBasis.length > 0)
        ? langBasis.map(l => l.toUpperCase()).join(' · ')
        : t('agreements.lifecycle.langMissing', { locales: (missingMandatory.length ? missingMandatory : mandatory).map(l => l.toUpperCase()).join(', ') }),
    },
    {
      id: 'publish', label: t('agreements.lifecycle.step.publish'),
      state: publishedExists ? 'done' : (workingDraft ? 'action' : 'pending'),
      detail: publishedExists
        ? t('agreements.lifecycle.publishedVn', { version: publishedVersion.version })
        : (workingDraft ? t('agreements.lifecycle.publishPending') : t('agreements.lifecycle.notPublished')),
    },
    {
      id: 'activate', label: t('agreements.lifecycle.step.activate'),
      state: publicNow ? 'done' : (publishedExists ? 'action' : 'pending'),
      detail: publicNow ? t('agreements.lifecycle.public') : t('agreements.lifecycle.notPublic'),
    },
    {
      id: 'usage', label: t('agreements.lifecycle.step.usage'),
      state: 'done',
      detail: bindingCount != null ? t('agreements.detail.bindingsCount', { count: bindingCount }) : undefined,
    },
  ];

  const canActivate = isSuperAdmin && publishedExists && !publicNow && meta.status !== 'archived';
  const canMakePrivate = isSuperAdmin && publicNow && meta.status !== 'archived';

  // Invokes ONLY the existing canonical content-update authority.
  const doStatusChange = async (newStatus, closeFn) => {
    setStatusSaving(true);
    try {
      await api.put(`/admin/content/${contentKey}`, { status: newStatus });
      toast.success(newStatus === 'active'
        ? t('agreements.activate.successActive', { key: contentKey })
        : t('agreements.activate.successPrivate', { key: contentKey }));
      closeFn(false);
      await load(); // refetch canonical state — no optimistic completion
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || t('agreements.activate.failed'));
    } finally {
      setStatusSaving(false);
    }
  };

  const doArchive = async () => {
    setArchiving(true);
    try {
      // Business-safe retirement — check dependencies BEFORE archiving.
      const ret = await api.get(`/admin/legal/documents/${encodeURIComponent(contentKey)}/retirement-check`);
      if (!ret.data?.safe_to_retire) {
        const blockers = [
          ...ret.data.blocked_by_bindings.map((b) => `• ${b.journey_label} (rule required)`),
          ...ret.data.blocked_by_packs.map((p) => `• Legal Pack ${p.pack_id}`),
        ].join('\n');
        toast.error(
          `This document cannot be retired yet. It is still used by:\n${blockers}\n\nRemove the rules or supersede the pack first.`,
          { duration: 10000 },
        );
        setArchiving(false);
        setArchiveOpen(false);
        return;
      }
      await api.post(`/admin/agreements/${contentKey}/archive`);
      toast.success('Document retired. Historical acceptances preserved.');
      setArchiveOpen(false);
      onArchive();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retirement failed');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div data-testid="agreement-detail-panel">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-xs text-slate-500 mb-1">{meta.content_key}</div>
          <h2 className="text-lg font-semibold text-slate-900" data-testid="agreement-detail-title">
            {(publishedVersion?.title || {}).en || meta.content_key}
          </h2>
          <p className="text-xs text-slate-500 mt-1">{(publishedVersion?.summary || {}).en || ''}</p>
        </div>
        <div className="space-x-2 flex-shrink-0">
          <Button size="sm" variant="outline" onClick={() => onNavigate(PANELS.EDITOR)} data-testid="agreement-edit-draft-btn">
            <Edit3 className="h-3 w-3 mr-1" /> Edit / New Draft
          </Button>
          <Button size="sm" variant="outline" onClick={() => onNavigate(PANELS.VERSIONS)} data-testid="agreement-versions-btn">
            <History className="h-3 w-3 mr-1" /> Versions
          </Button>
          <Button size="sm" variant="outline" onClick={() => onNavigate(PANELS.ACCEPTANCES)} data-testid="agreement-acceptances-btn">
            <ClipboardList className="h-3 w-3 mr-1" /> Acceptances
          </Button>
          <Button size="sm" variant="outline" onClick={() => onNavigate(PANELS.RECONSENT)} data-testid="agreement-reconsent-btn">
            <ShieldAlert className="h-3 w-3 mr-1" /> Re-consent
          </Button>
          <a
            href={`#/treatx-admin/legal-documents/usage?key=${encodeURIComponent(contentKey)}`}
            data-testid="agreement-usage-link"
            className="inline-flex items-center text-xs font-medium px-3 py-1.5 rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
          >
            Usage
          </a>
          <a
            href={`#/treatx-admin/legal-policies?doc=${encodeURIComponent(contentKey)}`}
            data-testid="agreement-assign-journey-link"
            className="inline-flex items-center text-xs font-medium px-3 py-1.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
          >
            Assign to process
          </a>
          {meta.status !== 'archived' && onOpenReplaceWizard && (
            <Button size="sm" variant="outline" onClick={onOpenReplaceWizard} data-testid="agreement-replace-btn">
              <Upload className="h-3 w-3 mr-1" /> New version / Replace
            </Button>
          )}
          {onOpenReconsent && (
            <Button size="sm" variant="outline" onClick={onOpenReconsent} data-testid="agreement-reconsent-config-btn">
              <ShieldAlert className="h-3 w-3 mr-1" /> Configure re-consent
            </Button>
          )}
          {meta.status !== 'archived' && (
            <Button size="sm" variant="outline" className="text-red-600" onClick={onOpenRetire || (() => setArchiveOpen(true))} data-testid="agreement-archive-btn">
              <Archive className="h-3 w-3 mr-1" /> Retire
            </Button>
          )}
        </div>
      </div>

      <LifecycleBar steps={lifecycleSteps} />

      {/* Public Status & Activation — publication is separate from binding */}
      <div className="bg-white border rounded-xl p-4 mb-4" data-testid="agreement-public-status-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs text-slate-500 uppercase mb-1">{t('agreements.library.publicStatus')}</div>
            {publicNow ? (
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700" data-testid="agreement-public-status-value">
                <CheckCircle2 className="h-4 w-4" /> {t('agreements.library.statusActive')}
                {publicRoute && (
                  <a href={`#${publicRoute}`} target="_blank" rel="noreferrer" className="ml-2 text-xs underline text-slate-500">{publicRoute}</a>
                )}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700" data-testid="agreement-public-status-value">
                <AlertTriangle className="h-4 w-4" /> {t('agreements.library.statusDraftNotPublic')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!publicNow && publishedExists && meta.status !== 'archived' && (
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => setActivateOpen(true)}
                disabled={!canActivate}
                title={isSuperAdmin ? '' : t('agreements.detail.superAdminOnly')}
                data-testid="agreement-activate-btn"
              >
                <Rocket className="h-3 w-3 mr-1" /> {t('agreements.activate.btn')}
              </Button>
            )}
            {publicNow && meta.status !== 'archived' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPrivateOpen(true)}
                disabled={!canMakePrivate}
                title={isSuperAdmin ? '' : t('agreements.detail.superAdminOnly')}
                data-testid="agreement-make-private-btn"
              >
                <EyeOff className="h-3 w-3 mr-1" /> {t('agreements.activate.makePrivateBtn')}
              </Button>
            )}
            {!isSuperAdmin && (publishedExists || publicNow) && meta.status !== 'archived' && (
              <span className="text-[11px] text-slate-400 inline-flex items-center gap-1" data-testid="agreement-superadmin-hint">
                <Lock className="h-3 w-3" /> {t('agreements.detail.superAdminOnly')}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border rounded-xl p-4">
          <div className="text-xs text-slate-500 uppercase mb-1">Metadata</div>
          <div className="text-sm space-y-1">
            <div><span className="text-slate-500">Audience:</span> <AudienceBadge audience={meta.audience} /></div>
            <div><span className="text-slate-500">Status:</span> <StatusBadge status={meta.status} /></div>
            <div><span className="text-slate-500">Updated:</span> {humanDate(meta.updated_at)}</div>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="text-xs text-slate-500 uppercase mb-1">{t('agreements.library.publishedVersion')}</div>
          {publishedVersion ? (
            <div className="text-sm space-y-1">
              <div><span className="text-slate-500">Version:</span> v{publishedVersion.version}</div>
              <div><span className="text-slate-500">Published:</span> {humanDate(publishedVersion.published_at || publishedVersion.effective_from || publishedVersion.effective_date)}</div>
              <div><span className="text-slate-500">Published by:</span> {publishedVersion.published_by || '—'}</div>
              <div className="break-all">
                <span className="text-slate-500">SHA-256 (en):</span>{' '}
                <code className="text-[10px] text-slate-700" data-testid="agreement-detail-sha">{shaEn || '—'}</code>
              </div>
            </div>
          ) : <div className="text-sm text-slate-400">No published version yet.</div>}
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="text-xs text-slate-500 uppercase mb-1">Changelog</div>
          <div className="text-sm text-slate-700 whitespace-pre-wrap">
            {publishedVersion?.changelog || '—'}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-white border rounded-xl p-4" data-testid="agreement-working-draft-card">
          <div className="text-xs text-slate-500 uppercase mb-1">{t('agreements.library.workingDraft')}</div>
          {workingDraft ? (
            <div className="text-sm text-slate-700" data-testid="agreement-working-draft-value">
              <span className="font-medium">v{workingDraft.version}</span> · {t('agreements.detail.workingDraftLabel')}
            </div>
          ) : (
            <div className="text-sm text-slate-400" data-testid="agreement-working-draft-value">{t('agreements.detail.noWorkingDraft')}</div>
          )}
          {supersededDrafts.length > 0 && (
            <div className="mt-2 text-xs text-slate-500" data-testid="agreement-superseded-drafts">
              {supersededDrafts.map(v => (
                <div key={v.version} data-testid={`agreement-superseded-draft-${v.version}`}>
                  v{v.version} · {t('agreements.detail.supersededDraft')}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border rounded-xl p-4" data-testid="agreement-usage-consent-card">
          <div className="text-xs text-slate-500 uppercase mb-1">{t('agreements.detail.usageTitle')}</div>
          <div className="text-sm space-y-1">
            <div><span className="text-slate-500">{t('agreements.detail.acceptanceMode')}:</span> {meta.acceptance_mechanism || 'passive'}</div>
            <div><span className="text-slate-500">{t('agreements.detail.usedByProcessRules')}:</span> {bindingCount != null ? bindingCount : t('agreements.lifecycle.unknown')}</div>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-4" data-testid="agreement-detail-body">
        <div className="text-xs text-slate-500 uppercase mb-2">Body (English)</div>
        <SafeHtml
          html={bodyEn || '<em>No body content.</em>'}
          className="prose prose-sm max-w-none text-slate-800"
        />
      </div>

      {/* OWNER UAT — Languages card. Reads STRICTLY from the canonical
          locale-overview endpoint (LegalMultilingualMetadata SSOT) and
          the currently-published version's body locale map. */}
      <div className="bg-white border rounded-xl p-4 mt-4" data-testid="agreement-languages-card">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <Globe2 className="h-4 w-4 text-slate-600" />
              {t('agreements.languages.card.title')}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {t('agreements.languages.card.description')}
            </p>
          </div>
          <a
            href={`#/treatx-admin/legal-multilingual?key=${encodeURIComponent(contentKey)}`}
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200"
            data-testid="agreement-languages-manage-btn"
          >
            <Globe2 className="h-3 w-3" /> Manage all languages
          </a>
        </div>
        {(() => {
          const pol = localeOverview?.policy || {};
          const pubBodyLocales = (localeOverview?.published?.locales_present) || [];
          const draftBodyLocales = (localeOverview?.draft?.locales_present) || [];
          const supported = localeOverview?._supported_locales || [];
          const locales = {
            published: pubBodyLocales,
            draft: draftBodyLocales,
            mandatory: pol.mandatory_locales || [],
            controlling: pol.controlling_locale || null,
          };
          // Union with the platform's supported locales so Owner sees
          // every clickable target (including Türkçe when body is empty).
          const list = Array.from(new Set([
            ...localeUnion(locales),
            ...supported,
          ]));
          const published = new Set(pubBodyLocales);
          const draft = new Set(draftBodyLocales);
          const mandatory = new Set(pol.mandatory_locales || []);
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="agreement-languages-grid">
              {list.map((loc) => {
                const label = LOCALE_LABEL[loc] || loc.toUpperCase();
                const isCtl = pol.controlling_locale === loc;
                const isMand = mandatory.has(loc);
                let stateLabel = t('agreements.languages.missing');
                let dotCls = 'bg-slate-300';
                let stateCls = 'text-slate-500';
                if (published.has(loc)) {
                  stateLabel = t('agreements.languages.published');
                  dotCls = 'bg-emerald-500';
                  stateCls = 'text-emerald-800';
                } else if (draft.has(loc)) {
                  stateLabel = t('agreements.languages.draft');
                  dotCls = 'bg-amber-500';
                  stateCls = 'text-amber-800';
                }
                return (
                  <div key={loc} className="flex items-center justify-between border rounded-lg px-3 py-2 bg-slate-50/40" data-testid={`agreement-language-card-${loc}`}>
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${dotCls}`} />
                      <div>
                        <div className="text-sm font-medium text-slate-800">{label}</div>
                        <div className={`text-[11px] ${stateCls}`}>
                          {stateLabel}
                          {isCtl && <span className="ml-1 text-slate-500">· {t('agreements.languages.card.controlling')}</span>}
                          {isMand && !isCtl && <span className="ml-1 text-slate-500">· {t('agreements.languages.card.mandatory')}</span>}
                        </div>
                      </div>
                    </div>
                    <a
                      href={`#/treatx-admin/legal-multilingual?key=${encodeURIComponent(contentKey)}&locale=${encodeURIComponent(loc)}`}
                      data-testid={`agreement-language-edit-${loc}`}
                      className="text-[11px] px-2 py-1 rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    >
                      {t('agreements.languages.edit')} {label}
                    </a>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {archiveOpen && (
        <ConfirmModal
          testId="agreement-archive-confirm"
          title="Retire this document?"
          message={`The document "${contentKey}" will no longer be used for new actions. Historical versions and acceptance evidence will remain preserved.`}
          confirmLabel={archiving ? 'Checking dependencies…' : 'Retire'}
          confirmDisabled={archiving}
          confirmDestructive
          onConfirm={doArchive}
          onCancel={() => setArchiveOpen(false)}
        />
      )}

      {activateOpen && (
        <ConfirmModal
          testId="agreement-activate-confirm"
          title={t('agreements.activate.title', { key: contentKey })}
          message={
            <div className="text-xs space-y-1">
              <div><strong>{t('agreements.activate.doc')}:</strong> {(publishedVersion?.title || {}).en || (publishedVersion?.title || {}).tr || contentKey} ({contentKey})</div>
              <div><strong>{t('agreements.activate.version')}:</strong> v{publishedVersion?.version}</div>
              <div><strong>{t('agreements.activate.locales')}:</strong> {(pubLocales || []).map(l => l.toUpperCase()).join(', ') || '—'}</div>
              {publicRoute && <div><strong>{t('agreements.activate.route')}:</strong> {publicRoute}</div>}
              <p className="pt-1">{t('agreements.activate.willBePublic')}</p>
              <p className="text-slate-500">{t('agreements.activate.noBindingChange')}</p>
            </div>
          }
          confirmLabel={statusSaving ? t('agreements.activate.activating') : t('agreements.activate.confirm')}
          confirmDisabled={statusSaving}
          onConfirm={() => doStatusChange('active', setActivateOpen)}
          onCancel={() => setActivateOpen(false)}
        />
      )}

      {privateOpen && (
        <ConfirmModal
          testId="agreement-make-private-confirm"
          title={t('agreements.activate.makePrivateTitle', { key: contentKey })}
          confirmDestructive
          message={
            <div className="text-xs space-y-1">
              {(bindingCount || 0) > 0 && (
                <div className="p-2 rounded bg-amber-50 text-amber-800 border border-amber-200" data-testid="agreement-private-warning">
                  <div className="font-semibold">{t('agreements.activate.privateWarnTitle')}</div>
                  {(bindingCount || 0) > 0 && <div>{t('agreements.activate.privateWarnBindings', { count: bindingCount })}</div>}
                </div>
              )}
              <p className="pt-1">{t('agreements.activate.privateWillHide')}</p>
              <p className="text-slate-500">{t('agreements.activate.privateNoBindingChange')}</p>
            </div>
          }
          confirmLabel={statusSaving ? t('agreements.activate.makingPrivate') : t('agreements.activate.confirmPrivate')}
          confirmDisabled={statusSaving}
          onConfirm={() => doStatusChange('draft', setPrivateOpen)}
          onCancel={() => setPrivateOpen(false)}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// 3. VERSION HISTORY
// ─────────────────────────────────────────────────────────────
const VersionsPanel = ({ contentKey }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/admin/agreements/${contentKey}/versions`);
        setVersions(r.data?.versions || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [contentKey]);

  return (
    <div data-testid="agreement-versions-panel">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-slate-900">Version History — {contentKey}</h2>
        <a
          href={`#/treatx-admin/legal-multilingual?key=${encodeURIComponent(contentKey)}`}
          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200"
          data-testid={`agreement-manage-locales-${contentKey}`}
        >
          <Users className="h-3 w-3" /> Manage Locales
        </a>
      </div>
      <div className="bg-white border rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2">Version</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Published</th>
              <th className="text-left px-4 py-2">Effective</th>
              <th className="text-left px-4 py-2">SHA-256 (en)</th>
              <th className="text-left px-4 py-2">Changelog</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && versions.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No versions yet.</td></tr>
            )}
            {versions.map((v) => (
              <tr key={v.version} className="border-t" data-testid={`agreement-version-row-${v.version}`}>
                <td className="px-4 py-3 font-medium">v{v.version}</td>
                <td className="px-4 py-3"><StatusBadge status={v.status || 'published'} /></td>
                <td className="px-4 py-3 text-xs text-slate-500">{humanDate(v.published_at)}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{humanDate(v.effective_from || v.effective_date)}</td>
                <td className="px-4 py-3">
                  <code className="text-[10px] text-slate-600">{shortSha((v.body_sha256 || {}).en)}</code>
                </td>
                <td className="px-4 py-3 text-xs text-slate-700 max-w-md truncate">{v.changelog || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// 4/5. DRAFT EDITOR + PUBLISH
// ─────────────────────────────────────────────────────────────
const DraftEditorPanel = ({ contentKey, onPublished }) => {
  const [existingDraft, setExistingDraft] = useState(null);
  const [publishedRef, setPublishedRef] = useState(null);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [acceptanceLabel, setAcceptanceLabel] = useState('');
  const [body, setBody] = useState('');
  const [changelog, setChangelog] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/agreements/${contentKey}/versions`);
      const versions = r.data?.versions || [];
      const draft = versions.find(v => v.status === 'draft');
      const published = versions.find(v => v.status === 'published') || versions[0];
      setExistingDraft(draft || null);
      setPublishedRef(published || null);
      if (draft) {
        setTitle((draft.title || {}).en || '');
        setSummary((draft.summary || {}).en || '');
        setAcceptanceLabel((draft.acceptance_label || {}).en || '');
        setBody((draft.body || {}).en || '');
        setChangelog(draft.changelog || '');
      } else {
        // Seed from published so admin can iterate rather than start empty.
        setTitle((published?.title || {}).en || '');
        setSummary((published?.summary || {}).en || '');
        setAcceptanceLabel((published?.acceptance_label || {}).en || '');
        setBody((published?.body || {}).en || '');
        setChangelog('');
      }
    } catch (e) {
      toast.error('Failed to load draft state');
    } finally {
      setLoading(false);
    }
  }, [contentKey]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!body.trim()) { toast.error('Body cannot be empty'); return; }
    setSaving(true);
    try {
      if (existingDraft) {
        const r = await api.patch(
          `/admin/agreements/${contentKey}/versions/${existingDraft.version}`,
          {
            title: { en: title },
            summary: { en: summary },
            body: { en: body },
            // Locale-safe: only the `en` key is sent, so a TR acceptance
            // label edited in the Multilingual Editor survives untouched.
            acceptance_label: { en: acceptanceLabel },
            changelog,
          },
        );
        setExistingDraft(r.data?.version || existingDraft);
        toast.success(`Draft v${existingDraft.version} saved`);
      } else {
        const r = await api.post(
          `/admin/agreements/${contentKey}/versions`,
          {
            title: { en: title },
            summary: { en: summary },
            body: { en: body },
            acceptance_label: { en: acceptanceLabel },
            changelog,
          },
        );
        setExistingDraft(r.data?.version || null);
        toast.success(`Draft v${r.data?.version?.version} created`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!existingDraft) return;
    setPublishing(true);
    try {
      await api.post(`/admin/agreements/${contentKey}/versions/${existingDraft.version}/publish`);
      toast.success(`Published v${existingDraft.version}`);
      setPublishOpen(false);
      onPublished(existingDraft.version);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return <div className="text-slate-500 py-10 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>;
  }

  return (
    <div data-testid="agreement-editor-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            {existingDraft ? `Edit Draft v${existingDraft.version}` : 'New Draft'}
          </h2>
          <p className="text-xs text-slate-500">
            {existingDraft
              ? 'A draft exists. Save updates, or Publish when ready.'
              : publishedRef
                ? `Seeded from currently-published v${publishedRef.version}. Changes create v${(publishedRef.version || 0) + 1}.`
                : 'Starting from empty draft.'}
          </p>
        </div>
        <div className="space-x-2">
          <Button size="sm" variant="outline" onClick={() => setPreview((p) => !p)} data-testid="agreement-preview-btn">
            <Eye className="h-3 w-3 mr-1" /> {preview ? 'Edit' : 'Preview'}
          </Button>
          <label className="inline-flex items-center gap-1 px-3 py-1.5 rounded border text-xs cursor-pointer hover:bg-slate-50" data-testid="agreement-docx-upload-label">
            <FileUp className="h-3 w-3"/> Upload DOCX
            <input type="file" accept=".docx" className="hidden" data-testid="agreement-docx-upload-input"
              onChange={async (e) => {
                const f = e.target.files?.[0]; if (!f) return;
                setSaving(true);
                try {
                  const fd = new FormData();
                  fd.append('file', f);
                  fd.append('changelog', changelog || `DOCX upload ${f.name}`);
                  const r = await api.post(`/admin/legal/documents/${contentKey}/versions/upload-docx`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                  toast.success(`Draft v${r.data.version} created from ${f.name}`);
                  await load();
                } catch (err) {
                  toast.error(err?.response?.data?.detail?.message || 'DOCX upload failed');
                } finally {
                  setSaving(false);
                  e.target.value = '';
                }
              }}
            />
          </label>
          <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid="agreement-save-draft-btn">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
            <span className="ml-1">{existingDraft ? 'Save Draft' : 'Create Draft'}</span>
          </Button>
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => setPublishOpen(true)}
            disabled={!existingDraft}
            data-testid="agreement-publish-btn"
          >
            <Send className="h-3 w-3 mr-1" /> Publish
          </Button>
        </div>
      </div>

      {preview ? (
        <div className="bg-white border rounded-xl p-6 space-y-3" data-testid="agreement-preview-body">
          <div className="text-lg font-semibold text-slate-900">{title || <em>Untitled</em>}</div>
          <div className="text-sm text-slate-500">{summary}</div>
          <SafeHtml
            html={body || '<em>No body content.</em>'}
            className="prose prose-sm max-w-none text-slate-800"
          />
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase text-slate-500 tracking-wide">Title (en)</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="agreement-title-input" />
          </div>
          <div>
            <label className="text-xs uppercase text-slate-500 tracking-wide">Summary (en)</label>
            <Input value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="agreement-summary-input" />
          </div>
          <div>
            <label className="text-xs uppercase text-slate-500 tracking-wide">
              Acceptance checkbox label (en)
            </label>
            <Input
              value={acceptanceLabel}
              onChange={(e) => setAcceptanceLabel(e.target.value)}
              placeholder="Required for explicit-checkbox documents — shown next to the checkbox"
              data-testid="agreement-acceptance-label-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase text-slate-500 tracking-wide">Body — HTML permitted (en)</label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={16}
              className="font-mono text-xs"
              data-testid="agreement-body-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase text-slate-500 tracking-wide">Changelog</label>
            <Textarea
              value={changelog}
              onChange={(e) => setChangelog(e.target.value)}
              rows={3}
              placeholder="What changed vs the previous published version?"
              data-testid="agreement-changelog-input"
            />
          </div>
        </div>
      )}

      {publishOpen && existingDraft && (
        <ConfirmModal
          testId="agreement-publish-confirm"
          title={`Publish draft v${existingDraft.version}?`}
          message={
            <>
              <p>Publishing will:</p>
              <ul className="list-disc list-inside text-xs mt-2 space-y-1">
                <li>Archive the currently-published version (if any).</li>
                <li>Make v{existingDraft.version} the new active version.</li>
                <li>Trigger the Re-consent Gate for every user whose latest acceptance is older.</li>
              </ul>
              <p className="mt-2 text-xs text-slate-500">
                The published version becomes immutable — further edits require a new draft.
              </p>
            </>
          }
          confirmLabel={publishing ? 'Publishing…' : 'Publish'}
          confirmDisabled={publishing}
          onConfirm={publish}
          onCancel={() => setPublishOpen(false)}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// 7. ACCEPTANCE RECORDS
// ─────────────────────────────────────────────────────────────
const AcceptancesPanel = ({ contentKey }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/admin/agreements/${contentKey}/acceptances`, { params: { limit: 250 } });
        setRows(r.data?.acceptances || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [contentKey]);

  return (
    <div data-testid="agreement-acceptances-panel">
      <h2 className="text-lg font-semibold text-slate-900 mb-3">Acceptance Records — {contentKey}</h2>
      <div className="bg-white border rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2">User</th>
              <th className="text-left px-4 py-2">Role</th>
              <th className="text-left px-4 py-2">Version</th>
              <th className="text-left px-4 py-2">SHA-256</th>
              <th className="text-left px-4 py-2">Accepted</th>
              <th className="text-left px-4 py-2">IP</th>
              <th className="text-left px-4 py-2">User-Agent</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">No acceptances yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.consent_id} className="border-t" data-testid={`acceptance-row-${r.consent_id}`}>
                <td className="px-4 py-3 text-xs">
                  <div className="text-slate-900">{r.user_email || <em className="text-slate-400">no email</em>}</div>
                  <div className="text-slate-400">{r.user_id}</div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">{r.user_type || '—'}</td>
                <td className="px-4 py-3">v{r.content_version}</td>
                <td className="px-4 py-3"><code className="text-[10px] text-slate-600">{shortSha(r.content_sha256)}</code></td>
                <td className="px-4 py-3 text-xs text-slate-500">{humanDate(r.consented_at)}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{r.ip_address || '—'}</td>
                <td className="px-4 py-3 text-xs text-slate-500 max-w-xs truncate" title={r.user_agent}>
                  {r.user_agent || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// 8. RE-CONSENT VISIBILITY
// ─────────────────────────────────────────────────────────────
const ReconsentPanel = ({ contentKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/agreements/${contentKey}/reconsent-summary`);
      setData(r.data || null);
    } finally { setLoading(false); }
  }, [contentKey]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="text-slate-500 py-10 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>;
  }
  if (!data) return <div className="text-red-600 py-10 text-center">Failed to load summary.</div>;

  return (
    <div data-testid="agreement-reconsent-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Re-consent Visibility — {contentKey}</h2>
          <p className="text-xs text-slate-500">
            Users whose latest acceptance is older than the currently-published version will be
            intercepted by ReconsentGate on their next authenticated request.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="agreement-reconsent-refresh"><RefreshCw className="h-3 w-3 mr-1" /> Refresh</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border rounded-xl p-4">
          <div className="text-xs text-slate-500 uppercase mb-1">Current Version</div>
          <div className="text-2xl font-semibold">v{data.current_version}</div>
          <div className="text-xs text-slate-500 mt-1">Audience: <AudienceBadge audience={data.audience} /></div>
        </div>
        <div className="bg-white border rounded-xl p-4" data-testid="agreement-reconsent-outdated">
          <div className="text-xs text-slate-500 uppercase mb-1">Re-consent Pending</div>
          <div className="text-2xl font-semibold text-amber-700">{data.reconsent_pending_users}</div>
          <div className="text-xs text-slate-500 mt-1">Unique users on an older version.</div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="text-xs text-slate-500 uppercase mb-1">Up-to-Date</div>
          <div className="text-2xl font-semibold text-emerald-700">{data.up_to_date_users}</div>
          <div className="text-xs text-slate-500 mt-1">Users on the current version.</div>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-4">
        <div className="text-xs text-slate-500 uppercase mb-2">Acceptance Breakdown</div>
        {(data.acceptance_breakdown || []).length === 0 ? (
          <div className="text-sm text-slate-500">No acceptances recorded yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left py-1">Version</th>
                <th className="text-left py-1">Unique Users</th>
                <th className="text-left py-1">State</th>
              </tr>
            </thead>
            <tbody>
              {data.acceptance_breakdown.map((row) => (
                <tr key={row.version} className="border-t" data-testid={`reconsent-breakdown-v${row.version}`}>
                  <td className="py-2">v{row.version}</td>
                  <td className="py-2">{row.unique_users}</td>
                  <td className="py-2 text-xs">
                    {row.version === data.current_version
                      ? <Badge className="bg-emerald-100 text-emerald-700">up-to-date</Badge>
                      : <Badge className="bg-amber-100 text-amber-700">outdated</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Confirm Modal (used by Publish + Archive)
// ─────────────────────────────────────────────────────────────
const ConfirmModal = ({ title, message, confirmLabel, confirmDisabled, confirmDestructive, onConfirm, onCancel, testId }) => (
  <div
    role="dialog"
    aria-modal="true"
    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
    data-testid={testId}
  >
    <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
      <div className="flex items-start gap-3 mb-3">
        <AlertTriangle className={`h-5 w-5 mt-0.5 ${confirmDestructive ? 'text-red-500' : 'text-amber-500'}`} />
        <div>
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <div className="text-sm text-slate-600 mt-1">{message}</div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" size="sm" onClick={onCancel} data-testid={`${testId}-cancel`}>Cancel</Button>
        <Button
          size="sm"
          className={confirmDestructive ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
          onClick={onConfirm}
          disabled={confirmDisabled}
          data-testid={`${testId}-confirm`}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────────────────────
const AdminAgreements = () => {
  const navigate = useNavigate();
  const { admin } = useTreatXAdminAuth();
  const isSuperAdmin = admin?.role === 'treatx_super_admin';
  const [agreements, setAgreements] = useState([]);
  const [supportedLocales, setSupportedLocales] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(true);
  const [libraryError, setLibraryError] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [panel, setPanel] = useState(PANELS.LIBRARY);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardPrefill, setWizardPrefill] = useState(null);
  const [retireOpen, setRetireOpen] = useState(false);
  const [reconsentOpen, setReconsentOpen] = useState(false);

  const loadLibrary = useCallback(async () => {
    setLibraryLoading(true);
    try {
      const [r, sl] = await Promise.all([
        api.get('/admin/agreements'),
        api.get('/admin/legal/supported-locales').catch(() => null),
      ]);
      setAgreements(r.data?.agreements || []);
      setSupportedLocales(sl?.data?.supported_locales || []);
      setLibraryError(null);
    } catch (e) {
      // Surface as an explicit LOAD ERROR — never let a failed request be
      // rendered as an innocuous "no agreements match filter" empty state.
      const resp = e?.response;
      setLibraryError({
        status: resp?.status || 0,
        detail: (resp?.data && (resp.data.detail || resp.data.message)) || e?.message || 'Request failed',
        correlation: resp?.headers?.['x-correlation-id'] || resp?.headers?.['cf-ray'] || null,
      });
      toast.error(resp?.data?.detail || 'Failed to load agreements');
    } finally {
      setLibraryLoading(false);
    }
  }, []);

  useEffect(() => { loadLibrary(); }, [loadLibrary]);

  const activeAgreement = useMemo(
    () => agreements.find(a => a.content_key === activeKey) || null,
    [agreements, activeKey],
  );

  const openAgreement = (key, targetPanel = PANELS.DETAIL) => {
    setActiveKey(key);
    setPanel(targetPanel);
  };

  const backToLibrary = () => {
    setActiveKey(null);
    setPanel(PANELS.LIBRARY);
    loadLibrary();
  };

  const afterPublish = () => {
    loadLibrary();
    setPanel(PANELS.RECONSENT);
  };

  const afterArchive = () => {
    backToLibrary();
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-agreements-page">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => (panel === PANELS.LIBRARY ? navigate('/treatx-admin') : backToLibrary())}
              className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
              data-testid="agreement-back-btn"
              aria-label="Back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-slate-600" />
              <h1 className="text-lg font-semibold text-slate-900">Agreement Management</h1>
            </div>
          </div>
          {!activeKey && isSuperAdmin && (
            <nav className="flex items-center gap-1 text-sm" data-testid="agreement-library-tabs">
              {[
                { id: PANELS.LIBRARY, label: 'Library' },
                { id: PANELS.REGISTRY, label: 'All records — Super Admin' },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setPanel(id)}
                  className={`px-3 py-1.5 rounded-md ${
                    panel === id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`agreement-tab-${id}`}
                >
                  {label}
                </button>
              ))}
            </nav>
          )}
          {activeKey && (
            <nav className="flex items-center gap-1 text-sm" data-testid="agreement-subnav">
              {[
                { id: PANELS.DETAIL, label: 'Detail', icon: FileText },
                { id: PANELS.EDITOR, label: 'Editor', icon: Edit3 },
                { id: PANELS.VERSIONS, label: 'Versions', icon: History },
                { id: PANELS.ACCEPTANCES, label: 'Acceptances', icon: Users },
                { id: PANELS.RECONSENT, label: 'Re-consent', icon: ShieldAlert },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setPanel(id)}
                  className={`px-3 py-1.5 rounded-md flex items-center gap-1 ${
                    panel === id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`agreement-subnav-${id}`}
                >
                  <Icon className="h-3 w-3" /> {label}
                </button>
              ))}
            </nav>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {activeKey && activeAgreement && (
          <div className="mb-4 text-xs text-slate-500" data-testid="agreement-breadcrumb">
            Library / <span className="text-slate-800 font-medium">{activeKey}</span>
          </div>
        )}
        {panel === PANELS.LIBRARY && (
          <LibraryPanel
            agreements={agreements}
            loading={libraryLoading}
            error={libraryError}
            onOpen={openAgreement}
            onReload={loadLibrary}
            onOpenWizard={() => { setWizardPrefill(null); setWizardOpen(true); }}
            supportedLocales={supportedLocales}
          />
        )}
        {panel === PANELS.REGISTRY && isSuperAdmin && (
          <LegalRegistryAllPanel />
        )}
        {panel === PANELS.DETAIL && activeKey && (
          <DetailPanel
            contentKey={activeKey}
            isSuperAdmin={isSuperAdmin}
            onNavigate={setPanel}
            onArchive={afterArchive}
            onOpenRetire={() => setRetireOpen(true)}
            onOpenReconsent={() => setReconsentOpen(true)}
            onOpenReplaceWizard={() => { setWizardPrefill({ mode: 'replace', content_key: activeKey }); setWizardOpen(true); }}
          />
        )}
        {panel === PANELS.VERSIONS && activeKey && (
          <VersionsPanel contentKey={activeKey} />
        )}
        {panel === PANELS.EDITOR && activeKey && (
          <DraftEditorPanel
            contentKey={activeKey}
            onPublished={afterPublish}
          />
        )}
        {panel === PANELS.ACCEPTANCES && activeKey && (
          <AcceptancesPanel contentKey={activeKey} />
        )}
        {panel === PANELS.RECONSENT && activeKey && (
          <ReconsentPanel contentKey={activeKey} />
        )}
      </div>

      <PublishWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        prefill={wizardPrefill}
        onPublished={() => { loadLibrary(); setWizardOpen(false); }}
      />
      <RetirementActionsModal
        open={retireOpen}
        onClose={() => setRetireOpen(false)}
        contentKey={activeKey}
        contentTitle={(activeAgreement?.title?.en) || (activeAgreement?.title?.tr) || activeKey}
        onDone={afterArchive}
      />
      <ReconsentConfigModal
        open={reconsentOpen}
        onClose={() => setReconsentOpen(false)}
        contentKey={activeKey}
        contentTitle={(activeAgreement?.title?.en) || (activeAgreement?.title?.tr) || activeKey}
      />
    </div>
  );
};

export default AdminAgreements;
